import { h } from 'preact';
import Store from 'store/store';
import { activateDomainTenant, cleanupDomainTenant, getDomainTenantStatus, prepareDomainTenant } from 'utils/domainMgmt';
import * as jsonPointer from 'utils/jsonPointer';
import { getIcon } from 'utils/icons';
import { confirm, Modal, toast } from 'utils/modal';
import { joinClasses } from 'utils/utils';

import Button from 'components/button/button';
import ContactLink from 'components/contactLink/contactLink';
import Spinner from 'components/spinner/spinner';

import s from 'components/domainSetUp/domainSetUp.sss';

const POLL_INTERVAL = 30000;
// Stages that are still in progress and should keep being polled
const POLLING_STAGES = ['awaiting_certificate', 'awaiting_activation', 'awaiting_dns_cutover'];

function copyToClipboard(value) {
	try {
		navigator.clipboard?.writeText?.(value);
		toast('Copied to clipboard', { icon: 'check', duration: 3000 });
	} catch {}
}

function DnsRecordsTable({ records }) {
	if (!records?.length) return null;
	return (
		<table class={s.dnsTable}>
			<thead>
				<tr>
					<th>Type</th>
					<th>Name</th>
					<th>Value</th>
					<th />
				</tr>
			</thead>
			<tbody>
				{records.map(record => (
					<tr key={`${record.stage}-${record.name}`}>
						<td>{record.type}</td>
						<td class={s.dnsValue}>{record.name}</td>
						<td class={s.dnsValue}>{record.value}</td>
						<td>
							<Button text type="button" small icon="copy" title="Copy value" onClick={() => copyToClipboard(record.value)} />
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

export default class DomainSetUp extends Modal {
	s = s;
	title = 'Set up custom domain';

	constructor(props) {
		super(props);

		this.state = {
			movieData: jsonPointer.get(Store.get().data, this.props.path),
			vanityDomain: '',
			loadingStatus: true,
		};

		this.cancel = this.cancel.bind(this);
		this.prepare = this.prepare.bind(this);
		this.activate = this.activate.bind(this);
		this.refreshStatus = this.refreshStatus.bind(this);
		this.onDomainInput = this.onDomainInput.bind(this);
		this.confirmCleanup = this.confirmCleanup.bind(this);
		this.performCleanup = this.performCleanup.bind(this);
		this.closeCleanupView = this.closeCleanupView.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();
		this.base.addEventListener('close', () => {
			this.unmounted = true;
			clearInterval(this.pollTimer);
		}, { once: true });
		this.refreshStatus();
	}

	componentWillUnmount() {
		super.componentWillUnmount?.();
		this.unmounted = true;
		clearInterval(this.pollTimer);
	}

	cancel() {
		this.base.close();
	}

	onDomainInput(e) {
		this.setState({ vanityDomain: e.target.value });
	}

	// The S3 origin prefix / title path is derived from the movie's thundr_id, which is only
	// assigned once the movie has been published at least once
	get titlePath() {
		const thundrId = this.state.movieData?.thundr_id;
		return thundrId == null ? null : String(thundrId);
	}

	schedulePoll(stage) {
		clearInterval(this.pollTimer);
		if (POLLING_STAGES.includes(stage)) {
			this.pollTimer = setInterval(this.refreshStatus, POLL_INTERVAL);
		}
	}

	async refreshStatus() {
		try {
			const status = await getDomainTenantStatus(this.props.path);
			if (this.unmounted) return;
			this.setState(prev => ({
				status,
				statusError: null,
				loadingStatus: false,
				vanityDomain: prev.vanityDomain || status?.vanityDomain || '',
			}));
			this.schedulePoll(status?.stage);
		} catch (err) {
			console.error(err);
			if (this.unmounted) return;
			this.setState({ statusError: { at: new Date(), error: err }, loadingStatus: false });
		}
	}

	async prepare() {
		const vanityDomain = this.state.vanityDomain.trim();
		if (!vanityDomain || !this.titlePath || this.state.preparing) return;

		this.setState({ preparing: true, prepareError: null });
		try {
			await prepareDomainTenant(this.props.path, vanityDomain);
			this.setState({ preparing: false });
		} catch (err) {
			console.error(err);
			this.setState({ prepareError: { at: new Date(), error: err }, preparing: false });
			return;
		}
		await this.refreshStatus();
	}

	async activate() {
		if (this.state.activating) return;

		this.setState({ activating: true, activateError: null });
		try {
			await activateDomainTenant(this.props.path);
		} catch (err) {
			// A 409 means the certificate isn't ISSUED yet - that's an expected "still waiting"
			// state, not a failure, so refreshStatus() below will re-render the waiting panel
			// instead of showing a generic error
			if (!err.notReady) {
				console.error(err);
				this.setState({ activateError: { at: new Date(), error: err } });
			}
		}
		this.setState({ activating: false });
		await this.refreshStatus();
	}

	async confirmCleanup() {
		const domain = this.state.status?.vanityDomain || this.state.vanityDomain;
		const ok = await confirm(
			`Are you sure you wish to remove the custom domain "${domain}"?\n\nThis tears down the CloudFront tenant, runtime config and certificate. You'll need to go through setup again to reassign it, and any DNS records at your registrar will need to be removed separately.`,
			{ title: 'Remove the custom domain?', button: 'Remove domain', buttonIcon: 'delete', danger: true }
		);
		if (!ok) return;
		this.setState({ cleanupView: true, cleanupResult: null, cleanupError: null });
		await this.performCleanup();
	}

	async performCleanup() {
		this.setState({ cleaningUp: true, cleanupError: null });
		try {
			const result = await cleanupDomainTenant(this.props.path);
			if (this.unmounted) return;
			this.setState({ cleaningUp: false, cleanupResult: result });
			// Once the certificate is actually gone too (not just "in_use"), the movie is back to
			// not_prepared - refresh so the status behind this view is accurate once it's closed
			if (result?.certificate !== 'in_use') {
				await this.refreshStatus();
			}
		} catch (err) {
			console.error(err);
			if (this.unmounted) return;
			this.setState({ cleaningUp: false, cleanupError: { at: new Date(), error: err } });
		}
	}

	closeCleanupView() {
		this.setState({ cleanupView: false, cleanupResult: null, cleanupError: null });
	}

	renderError(error, subject) {
		if (!error) return null;
		const ContactUsLink = () => (
			<ContactLink
				subject={subject}
				body={[
					`Error at ${error.at.toISOString()} for movie path ${this.props.path}\n`,
					`Browser: ${navigator.userAgent}\n`,
					'Error details:',
					error.error?.message,
					error.error?.stack
				].filter(e => e).join('\n') + '\n'}
			>
				contact us
			</ContactLink>
		);
		return (
			<div class={s.error}>
				Sorry, an error occurred. Please try again later or <ContactUsLink /> if the problem persists.
			</div>
		);
	}

	renderNotPrepared() {
		const { prepareError, preparing, vanityDomain } = this.state;
		return (
			<div class={joinClasses(s.content, prepareError && s.hasError)}>
				<div>
					This will provision a dedicated CloudFront distribution and SSL certificate so this movie can be served on a custom domain.
				</div>
				{!this.titlePath && (
					<div class={s.hint}>This movie needs to be published at least once before a custom domain can be set up.</div>
				)}
				<label>
					<div class={s.name}>Domain</div>
					<input
						class={s.field}
						type="text"
						placeholder="watch.mymovie.com"
						value={vanityDomain}
						disabled={preparing}
						onInput={this.onDomainInput}
					/>
				</label>
				{this.renderError(prepareError, 'Error while setting up custom domain')}
			</div>
		);
	}

	renderTrafficPreview(trafficRecords) {
		if (!trafficRecords?.length) return null;
		return (
			<div>
				<div class={s.hint}>
					Once the domain is activated, point this record at CloudFront to go live. You can add it with your DNS provider ahead of time, but don't switch it until the domain is fully activated — switching early will serve a CloudFront error instead of the site.
				</div>
				<DnsRecordsTable records={trafficRecords} />
			</div>
		);
	}

	renderAwaitingCertificate() {
		const { status } = this.state;
		const validationRecords = (status?.dnsRecords || []).filter(record => record.stage === 'validation');
		const trafficRecords = (status?.dnsRecords || []).filter(record => record.stage === 'traffic');
		const hasApex = trafficRecords.some(record => record.type === 'ALIAS');
		return (
			<div class={s.content}>
				<div>Waiting for DNS validation before the certificate can be issued.</div>
				<div class={s.hint}>Certificate status: {status?.certificateStatus || 'PENDING_VALIDATION'}</div>
				<div>
					Add this record with your DNS provider now, and leave it in place permanently — ACM reuses it for automatic renewal.
				</div>
				<DnsRecordsTable records={validationRecords} />
				{hasApex && (
					<div class={s.hint}>This is an apex domain: use an ALIAS/ANAME record instead of a CNAME if your DNS provider requires it.</div>
				)}
				{this.renderTrafficPreview(trafficRecords)}
			</div>
		);
	}

	renderAwaitingActivation() {
		const { activateError, status } = this.state;
		const trafficRecords = (status?.dnsRecords || []).filter(record => record.stage === 'traffic');
		return (
			<div class={joinClasses(s.content, activateError && s.hasError)}>
				<div>The certificate has been issued. Click Activate to enable the domain on CloudFront.</div>
				{this.renderTrafficPreview(trafficRecords)}
				{this.renderError(activateError, 'Error while activating custom domain')}
			</div>
		);
	}

	renderCleanup() {
		const { cleaningUp, cleanupError, cleanupResult } = this.state;

		if (cleaningUp) {
			return (
				<div class={joinClasses(s.content, s.loading)}>
					<Spinner /> Removing domain…
				</div>
			);
		}

		if (cleanupError) {
			return (
				<div class={joinClasses(s.content, s.hasError)}>
					<div>Sorry, something went wrong while removing the domain.</div>
					{this.renderError(cleanupError, 'Error while removing custom domain')}
				</div>
			);
		}

		if (cleanupResult?.certificate === 'in_use') {
			return (
				<div class={s.content}>
					<div>{cleanupResult.message}</div>
					<div class={s.hint}>
						The domain has been fully torn down — only the certificate remains, since CloudFront was still referencing it right after the tenant was deleted. Wait 30–60 seconds, then retry to finish deleting it.
					</div>
				</div>
			);
		}

		return (
			<div class={s.content}>
				<div>{cleanupResult?.message || 'Domain infrastructure removed.'}</div>
				<div class={s.hint}>
					DNS was not touched — remember to remove the validation and traffic CNAME records{cleanupResult?.vanityDomain ? ` for ${cleanupResult.vanityDomain}` : ''} at your registrar, otherwise they'll keep pointing at a CloudFront endpoint that no longer serves this movie.
				</div>
			</div>
		);
	}

	renderAwaitingCutover() {
		const { status } = this.state;
		const trafficRecords = (status?.dnsRecords || []).filter(record => record.stage === 'traffic');
		const dnsCheck = status?.dnsCheck;
		return (
			<div class={s.content}>
				<div>The domain is enabled on CloudFront. Point your traffic DNS record here to go live.</div>
				<div class={s.hint}>
					Only add this record once you're ready to cut over — pointing it early will serve a CloudFront error instead of the site. That's fine for a brand-new domain, but plan ahead if migrating a domain that's already live.
				</div>
				<DnsRecordsTable records={trafficRecords} />
				{dnsCheck && (
					<div class={s.hint}>
						{dnsCheck.resolvedCname ? `Currently resolves to: ${dnsCheck.resolvedCname}` : 'No traffic record detected yet.'}
						{dnsCheck.pointsToCloudFront ? ' — pointing to CloudFront, this should go active shortly.' : ''}
					</div>
				)}
			</div>
		);
	}

	renderActive() {
		const { status, vanityDomain } = this.state;
		return (
			<div class={s.content}>
				<div class={s.success}>
					<span class={s.successIcon}>{getIcon('check')}</span>
					{status?.vanityDomain || vanityDomain} is live.
				</div>
			</div>
		);
	}

	renderContent() {
		if (this.state.cleanupView) {
			return this.renderCleanup();
		}

		const { loadingStatus, statusError } = this.state;

		if (loadingStatus) {
			return (
				<div class={joinClasses(s.content, s.loading)}>
					<Spinner /> Loading domain status…
				</div>
			);
		}
		if (statusError) {
			return (
				<div class={joinClasses(s.content, s.hasError)}>
					<div>Sorry, we couldn't load the domain status for this movie.</div>
					{this.renderError(statusError, 'Error while loading custom domain status')}
				</div>
			);
		}

		const stage = this.state.status?.stage || 'not_prepared';
		switch (stage) {
			case 'awaiting_certificate': return this.renderAwaitingCertificate();
			case 'awaiting_activation': return this.renderAwaitingActivation();
			case 'awaiting_dns_cutover': return this.renderAwaitingCutover();
			case 'active': return this.renderActive();
			default: return this.renderNotPrepared();
		}
	}

	renderCleanupActions() {
		const { cleaningUp, cleanupError, cleanupResult } = this.state;

		if (cleaningUp) {
			return <div class={this.c('actions')}><Button type="button" disabled key="removing">Removing…</Button></div>;
		}

		if (cleanupError) {
			return (
				<div class={this.c('actions')}>
					<Button type="button" onClick={this.closeCleanupView} key="back">Back</Button>
					<Button danger type="button" onClick={this.performCleanup} key="retry">Retry</Button>
				</div>
			);
		}

		if (cleanupResult?.certificate === 'in_use') {
			return (
				<div class={this.c('actions')}>
					<Button type="submit" value="CANCEL" key="cancel">Close</Button>
					<Button primary type="button" onClick={this.performCleanup} key="retry">Retry cleanup</Button>
				</div>
			);
		}

		return (
			<div class={this.c('actions')}>
				<Button primary type="button" onClick={this.closeCleanupView} key="done">Done</Button>
			</div>
		);
	}

	renderActions() {
		if (this.state.cleanupView) {
			return this.renderCleanupActions();
		}

		const { activating, loadingStatus, preparing, statusError, vanityDomain } = this.state;
		const stage = this.state.status?.stage || 'not_prepared';

		const cancelButton = <Button type="submit" value="CANCEL" key="cancel">{stage === 'active' ? 'Close' : 'Cancel'}</Button>;
		const refreshButton = POLLING_STAGES.includes(stage) && (
			<Button text type="button" icon="reset" onClick={this.refreshStatus} key="refresh">Refresh now</Button>
		);
		const removeButton = stage !== 'not_prepared' && (
			<Button outline danger class={s.removeButton} type="button" icon="delete" onClick={this.confirmCleanup} key="remove">Remove domain</Button>
		);

		if (loadingStatus || statusError) {
			return <div class={this.c('actions')}>{cancelButton}</div>;
		}

		if (stage === 'not_prepared') {
			return (
				<div class={this.c('actions')}>
					{cancelButton}
					<Button
						primary
						type="button"
						icon="link"
						loading={preparing}
						disabled={!vanityDomain.trim() || !this.titlePath || preparing}
						onClick={this.prepare}
						key="prepare"
					>
						{preparing ? 'Setting up…' : 'Set up domain'}
					</Button>
				</div>
			);
		}

		if (stage === 'awaiting_activation') {
			return (
				<div class={this.c('actions')}>
					{removeButton}
					{cancelButton}
					{refreshButton}
					<Button
						primary
						type="button"
						icon="rocket"
						loading={activating}
						disabled={activating}
						onClick={this.activate}
						key="activate"
					>
						{activating ? 'Activating…' : 'Activate'}
					</Button>
				</div>
			);
		}

		return (
			<div class={this.c('actions')}>
				{removeButton}
				{cancelButton}
				{refreshButton}
			</div>
		);
	}
}
